import {createEventEmmiter, IEventEmitter, StringUtils} from 'neo-common';
import {BetData, Help, History, Paytable, RoundEnded, RoundStarted} from '../../Platforms/IPlatforms';
import {AppContextBase, GameConfigurationBase} from '../../GameEngine';

export class EventTracker {

    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly EVENTS_TRACKER: string = 'onEventsTracker';

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    public static readonly DELAY_TIME: number = 10; //in seconds

    //******************************************************************************************************************
    //  EVENTS FILTER
    //******************************************************************************************************************

    private static readonly EventsList = {
        GameRoundEvents: ['GameRoundEnded'],
        SessionEvents: ['GameGoToPaytable', 'GameGoToHelp', 'GameGoToHistory', 'GameGoToSettings', 'GameQuickSpinDialog'],
        DelayedEvents: ['GameSetVolume', 'GameBetChange'],
        InstantEvents: ['GameExit'],
        GameReadyEvents: ['RendererType', 'ScreenSize', 'SyncDiff']
    }

    protected enabledEvents: Array<string>;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected screenSize: {width: number, height: number};

    protected eventsPerSession: Array<string>;

    protected eventsPerGameRoundsTemp: {[id: string]: string};

    protected eventsPerGameRounds: {[id: string]: string};

    protected eventsAfterDelay: {[id: string]: EventData};

    protected gameReadyEvents: Array<EventData>;

    protected afterDelayTimerId: number;

    protected eventEmitter: IEventEmitter;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        this.eventEmitter = createEventEmmiter();

        this.eventsPerSession = [];
        this.eventsPerGameRoundsTemp = {};
        this.eventsPerGameRounds = {};
        this.eventsAfterDelay = {};
        this.gameReadyEvents = [];
        this.enabledEvents = [];

        const appContext = AppContextBase.getInstance();
        if (appContext.gameConfiguration != null) {
            this.enabledEvents = this.getEnabledEvents(appContext.gameConfiguration.EventTrackerEnabledEvents);
        }

        this.initEventsPerGameRounds();
    }

    protected getEnabledEvents(config: string): Array<string> {
        const result: Array<string> = [];

        if (config === '') {
            return [];
        }

        const splittedConfig = config.split(' ');
        if (splittedConfig.indexOf('*') > -1) {
            return [...EventTracker.EventsList.GameRoundEvents,
                ...EventTracker.EventsList.SessionEvents,
                ...EventTracker.EventsList.DelayedEvents,
                ...EventTracker.EventsList.InstantEvents,
                ...EventTracker.EventsList.GameReadyEvents];
        }

        for (let i = 0; i < splittedConfig.length; i++) {
            let category = splittedConfig[i].split(':');
            let categoryData: Array<string> = [];

            if (category.length === 2) {
                categoryData = category[1].split('|');
            }

            const events = EventTracker.EventsList[category[0]];
            if (events != null) {
                if (categoryData.length > 0) {
                    for (let j = 0; j < categoryData.length; j++) {
                        if (events.indexOf(categoryData[j]) > -1) {
                            result.push(categoryData[j]);
                        }
                    }
                } else {
                    result.push(...events);
                }
            }
        }

        return result;
    }

    private initEventsPerGameRounds(): void {
        this.eventsPerGameRounds['GameLeftHandMode'] = document.body.classList.contains('reverse').toString();
        this.eventsPerGameRounds['GamePortraitMode'] = document.body.classList.contains('portrait').toString();
        this.eventsPerGameRounds['GameAutoplay'] = 'false';
        this.eventsPerGameRounds['GameAutoplayStop'] = 'false';
        this.eventsPerGameRounds['GameQuickSpin'] = 'false';
        this.eventsPerGameRounds['UserAction'] = 'false';
    }

    //******************************************************************************************************************
    //  TRACKING - GAME ROUND EVENTS
    //******************************************************************************************************************

    quickSpinEnabled(enabled: boolean): void {
        this.eventsPerGameRounds['GameQuickSpin'] = enabled.toString();
    }

    autoplay(enabled: boolean): void {
        this.eventsPerGameRounds['GameAutoplay'] = enabled.toString();
    }

    autoplayStop(): void {
        this.eventsPerGameRounds['GameAutoplayStop'] = 'true';
    }

    roundStarted(args?: RoundStarted): void {
        if (args == null) {
            return;
        }

        this.eventsPerGameRounds['UserAction'] = args.userAction.toString();
    }

    roundEnded(args?: RoundEnded): void {
        this.eventsPerGameRounds['GameLeftHandMode'] = document.body.classList.contains('reverse').toString();
        this.eventsPerGameRounds['GamePortraitMode'] = document.body.classList.contains('portrait').toString();

        let updatedGameRoundData = this.getUpdatedGameRoundsData();
        let eventsArray = [this.createTrackedEvent('GameRoundEnded', 'data', null, updatedGameRoundData)];

        if (this.screenSize.width !== window.innerWidth || this.screenSize.height !== window.innerHeight) {
            this.screenSize = {width: window.innerWidth, height: window.innerHeight};
            eventsArray.push(this.createTrackedEvent('ScreenSize', 'data', null, this.screenSize));
        }

        //send and clear events after delay
        if (this.afterDelayTimerId > -1) {
            clearTimeout(this.afterDelayTimerId);
            eventsArray = this.createArrayOfEvents(this.eventsAfterDelay).concat(eventsArray);
            this.eventsAfterDelay = {};
        }

        this.sendEvents(eventsArray);

        //reset some of the events
        this.eventsPerGameRounds['GameAutoplayStop'] = 'false';
        this.updateGameRoundsTempData();
    }

    private getUpdatedGameRoundsData(): {[id: string]: string} {
        let result: {[id: string]: string} = {};

        for (let key in this.eventsPerGameRounds) {
            if (this.eventsPerGameRounds[key] !== this.eventsPerGameRoundsTemp[key]) {
                result[key] = this.eventsPerGameRounds[key];
            }
        }

        return result;
    }

    private updateGameRoundsTempData(): void {
        this.eventsPerGameRoundsTemp = {};

        for (let key in this.eventsPerGameRounds) {
            this.eventsPerGameRoundsTemp[key] = this.eventsPerGameRounds[key];
        }
    }

    //******************************************************************************************************************
    //  TRACKING - ONCE PER SESSION EVENTS
    //******************************************************************************************************************

    gotoPaytable(args?: Paytable): void {
        if (this.eventsPerSession.indexOf('GameGoToPaytable') > -1) {
            return;
        }

        this.eventsPerSession.push('GameGoToPaytable');
        const data = this.createTrackedEvent('GameGoToPaytable', 'true');
        this.sendEvents([data]);
    }

    gotoHelp(args?: Help): void {
        if (this.eventsPerSession.indexOf('GameGoToHelp') > -1) {
            return;
        }

        this.eventsPerSession.push('GameGoToHelp');
        const data = this.createTrackedEvent('GameGoToHelp', 'true');
        this.sendEvents([data]);
    }

    gotoHistory(args?: History): void {
        if (this.eventsPerSession.indexOf('GameGoToHistory') > -1) {
            return;
        }

        this.eventsPerSession.push('GameGoToHistory');
        const data = this.createTrackedEvent('GameGoToHistory', 'true');
        this.sendEvents([data]);
    }

    gotoSettings(): void {
        if (this.eventsPerSession.indexOf('GameGoToSettings') > -1) {
            return;
        }

        this.eventsPerSession.push('GameGoToSettings');
        const data = this.createTrackedEvent('GameGoToSettings', 'true');
        this.sendEvents([data]);
    }

    quickSpinDialog(result: boolean): void {
        if (this.eventsPerSession.indexOf('GameQuickSpinDialog') > -1) {
            return;
        }

        this.eventsPerSession.push('GameQuickSpinDialog');
        const data = this.createTrackedEvent('GameQuickSpinDialog', result.toString());
        this.sendEvents([data]);
    }

    //******************************************************************************************************************
    //  TRACKING - EVENTS WITH DELAY
    //******************************************************************************************************************

    setVolume(level: number): void {
        const _self = this;

        if (this.afterDelayTimerId > -1) {
            clearTimeout(this.afterDelayTimerId);
        }

        this.eventsAfterDelay['GameSetVolume'] = this.createTrackedEvent('GameSetVolume', null, level);

        this.afterDelayTimerId = setTimeout(() => {
            _self.sendEvents(_self.createArrayOfEvents(_self.eventsAfterDelay));
            _self.eventsAfterDelay = {};
        }, EventTracker.DELAY_TIME * 1000);
    }

    bet(betData: BetData): void {
        const _self = this;

        if (this.afterDelayTimerId > -1) {
            clearTimeout(this.afterDelayTimerId);
        }

        this.eventsAfterDelay['GameBetChange'] = this.createTrackedEvent('GameBetChange', 'data', null, betData);

        this.afterDelayTimerId = setTimeout(() => {
            _self.sendEvents(_self.createArrayOfEvents(_self.eventsAfterDelay));
            _self.eventsAfterDelay = {};
        }, EventTracker.DELAY_TIME * 1000);
    }

    //******************************************************************************************************************
    //  TRACKING - INSTANT EVENTS
    //******************************************************************************************************************

    //ToDo add information about place, where exit was emitted
    gameClosing(exitHandler: string): void {

        //clear timeout for delayed events, because we want to send them on exit
        if (this.afterDelayTimerId > -1) {
            clearTimeout(this.afterDelayTimerId);
        }

        this.sendEvents([this.createTrackedEvent('GameExit', exitHandler)].concat(this.createArrayOfEvents(this.eventsAfterDelay)));
        this.eventsAfterDelay = {}; //not necessary
    }

    //******************************************************************************************************************
    //  TRACKING - GAME READY EVENTS
    //******************************************************************************************************************

    rendererType(rendererType: string): void {
        this.gameReadyEvents.push(this.createTrackedEvent('RendererType', rendererType));
    }

    clockSync(syncDiff: number): void {
        this.gameReadyEvents.push(this.createTrackedEvent('SyncDiff', null, syncDiff));
    }

    gameReady(): void {
        this.screenSize = {width: window.innerWidth, height: window.innerHeight};
        this.gameReadyEvents.push(this.createTrackedEvent('ScreenSize', 'data', null, this.screenSize));

        this.sendEvents(this.gameReadyEvents);
    }

    //******************************************************************************************************************
    //  CREATE EVENT
    //******************************************************************************************************************

    protected createTrackedEvent(eventType: string, label?: string, value?: number, customData?: any): EventData {
        let eventData = {
            eventType: eventType
        } as EventData;

        if (!StringUtils.isNullOrEmpty(label)) {
            eventData.label = label;
        }

        if (value != null) {
            eventData.value = value;
        }

        if (customData != null) {
            eventData.customData = customData;
        }

        return eventData;
    }

    protected createArrayOfEvents(events: {[id: string]: EventData}): Array<EventData> {
        const eventsArray: Array<EventData> = [];

        for (let key in events) {
            if (events[key] != null) {
                eventsArray.push(events[key]);
            }
        }

        return eventsArray;
    }

    protected filterEnabledEvents(events: Array<EventData>): Array<EventData> {
        const result: Array<EventData> = [];

        for (let i = 0; i < events.length; i++) {
            const event = events[i];

            if (this.enabledEvents.indexOf(event.eventType) > -1) {
                result.push(event);
            }
        }

        return result;
    }

    //******************************************************************************************************************
    //  EVENTS HANDLING
    //******************************************************************************************************************

    private sendEvents(events: Array<EventData>): void {
        if (events == null || events.length === 0) {
            return;
        }

        events = this.filterEnabledEvents(events);

        this.emit(EventTracker.EVENTS_TRACKER, events);
    }

    on(event: string, context?: any): void {
        this.eventEmitter.on(event, context);
    }

    protected emit(event: string, args: Array<EventData>): void {
        this.eventEmitter.emit(event, args);
    }
}

export type EventData = {
    eventType: string;
    label?: string;
    value?: number;
    customData?: any;
}


/** WEBPACK FOOTER **
 ** ./Engine/Plugins/EventTracker/EventTracker.ts
 **/